import * as React from 'react';
import {
  GameRow, 
  EnteredGameRow, 
  StartWordRow, 
  EndWordRow,
} from './GameRow.tsx';
import { GameType } from '../../types/GameType.ts';

import './GameBoard.css';

interface GameBoardProps {
  startWord: string;
  endWord: string;
  enteredWords: string[];
  currentWord: string;
  isGameOver: boolean;
  onClickClearBoard: () => void;
  gameType: GameType;
  currentMinWordLength: number;
  currentMaxWordLength: number;
  rowLength: number;
}

class GameBoard extends React.Component<GameBoardProps> {
  private inputRowsEnd: HTMLDivElement | null = null;
  private previousEnteredWordsLength: number = 0;

  scrollToBottom = (): void => {
    // Scroll to the bottom of the game board (to the inputRowsEnd element)
    if (this.inputRowsEnd) {
      this.inputRowsEnd.scrollIntoView({ behavior: "smooth" });
    }
  }

  scrollBackToGameBoard = (): void => {
    // When rows are removed, scroll back to the game board
    // This effectively scrolls "up" to where the game board now ends
    const gameBoard = document.querySelector('.gameBoard');
    if (gameBoard) {
      gameBoard.scrollIntoView({ behavior: "smooth" });
    }
  }

  componentDidUpdate(): void {
    const currentLength = this.props.enteredWords.length;
    
    // Check if we added or removed rows by comparing with previous length
    if (currentLength > this.previousEnteredWordsLength) {
      // Added rows - scroll down to the bottom
      this.scrollToBottom();
    } else if (currentLength < this.previousEnteredWordsLength) {
      // Removed rows - scroll back to the game board (which appears as scrolling "up")
      this.scrollBackToGameBoard();
    }
    
    // Update the previous length for next comparison
    this.previousEnteredWordsLength = currentLength;
    
    document.dispatchEvent(new Event('scroll'));
  }

  getLastEnteredWordOrStart(): string {
    if (this.props.enteredWords.length > 0) {
      return this.props.enteredWords.slice(-1)[0];
    } else {
      return this.props.startWord;
    }
  }
  
  isWeaverXGame(): boolean {
    return this.props.gameType === GameType.WEAVER_X;
  }

  render(): React.ReactNode {
    // For regular WEAVER, both words must be the same length
    // For WEAVER_X, we allow different lengths
    if (!this.isWeaverXGame() && this.props.endWord.length !== this.props.startWord.length) {
      return (
        <div>Something is wrong...apologies!</div>
      );
    }
    
    const lastWord = this.getLastEnteredWordOrStart();

    return (
      <div className={`gameBoard ${this.isWeaverXGame() ? 'weaverX' : ''}`}>
        <div className='startWordRowContainer'>
          <StartWordRow 
            rowLength={this.props.rowLength}
            word={this.props.startWord} 
          />
        </div>
        <div className='inputRowsContainer'>
          {this.props.enteredWords.map((word, index, words) =>
            <EnteredGameRow
              rowLength={this.props.rowLength}
              key={index} 
              word={word} 
              previousWord={
                index > 0 ? words[index - 1] : this.props.startWord
              }
              endWord={this.props.endWord}
              gameType={this.props.gameType}
            />
          )}
          {!this.props.isGameOver ? 
            <GameRow
              rowLength={this.props.rowLength}
              word={this.props.currentWord} 
              minWordLength={this.props.currentMinWordLength}
              maxWordLength={this.props.currentMaxWordLength}
            />
            : null
          }
          <div className='inputRowsEndElement' ref={(el) => { this.inputRowsEnd = el; }}>
          </div>
        </div>
        <div className='endWordRowContainer'>
          <EndWordRow
            rowLength={this.props.rowLength}
            word={this.props.endWord} 
            lastGameWord={lastWord} 
            isBouncingBlock={this.props.isGameOver}
          />
        </div>
        {this.props.enteredWords.length > 0 &&
          <button
            className='clearBoardButton'
            onClick={() => this.props.onClickClearBoard()}>
            Reset
          </button>
        }
      </div>
    );
  }
}

export default GameBoard;